<script>
import GeneralAnalyticsFilters from '@/components/page-user-analytics/GeneralAnalyticsFilters.vue';
import GeneralAnalyticsObject from '@/components/page-user-analytics/GeneralAnalyticsObject.vue';
import RefreshStatusCard from '@/components/page-user-analytics/RefreshStatusCard.vue';
import PaginationController from '@/components/base-components/PaginationController.vue';

export default {
    components: {
        GeneralAnalyticsFilters,
        GeneralAnalyticsObject,
        RefreshStatusCard,
        PaginationController,
    },
    data() {
        return {
            sortColumns: {
                userIdSort: { field: 'user_id', direction: 'desc' },
                emailSort: { field: 'user_email', direction: null },
                downloadsSort: { field: 'total_downloads', direction: null },
                uniqueDownloadsSort: { field: 'unique_files_downloaded', direction: null },
                collectionsSort: { field: 'collections_created', direction: null },
                zipDownloadsSort: { field: 'zip_downloads', direction: null },
                activeDaysSort: { field: 'total_active_days', direction: null },
                lastActivitySort: {
                    field: 'last_activity_date',
                    direction: null,
                },
            },
        };
    },
    computed: {
        analytics() {
            return this.$store.getters.USER_ANALYTICS;
        },
        isLoading() {
            return this.$store.getters.USER_ANALYTICS_LOADING;
        },
        isExporting() {
            return this.$store.getters.USER_ANALYTICS_IS_EXPORTING;
        },
        pagination() {
            return this.$store.getters.USER_ANALYTICS_PAGINATION;
        },
        currentPage() {
            return this.$store.getters.USER_ANALYTICS_CURRENT_PAGE;
        },
        totalPages() {
            return this.$store.getters.USER_ANALYTICS_TOTAL_PAGES;
        },
        maxPage() {
            return this.totalPages > 0 ? this.totalPages - 1 : 0;
        },
        displayRange() {
            const start =
                (this.pagination.page - 1) * this.pagination.perPage + 1;
            const end = Math.min(
                this.pagination.page * this.pagination.perPage,
                this.pagination.total
            );
            return `${start}-${end} of ${this.pagination.total}`;
        },
        refreshStatus() {
            return this.$store.getters.USER_ANALYTICS_REFRESH_STATUS;
        },
        isRefreshing() {
            return this.$store.getters.USER_ANALYTICS_IS_REFRESHING;
        },
    },
    methods: {
        handleSort(columnId) {
            const column = this.sortColumns[columnId];
            if (!column) return;

            // Cycle through: null -> asc -> desc -> null
            if (column.direction === null) {
                column.direction = 'asc';
            } else if (column.direction === 'asc') {
                column.direction = 'desc';
            } else {
                column.direction = null;
            }

            // Reset other columns (single-column sort)
            Object.keys(this.sortColumns).forEach((key) => {
                if (key !== columnId) {
                    this.sortColumns[key].direction = null;
                }
            });

            // Determine sort values
            const sortBy = column.direction ? column.field : 'user_id';
            const sortOrder = column.direction || 'asc';

            this.$store.commit('SET_USER_ANALYTICS_SORT', {
                sortBy,
                sortOrder,
            });
            this.$store.commit('SET_USER_ANALYTICS_PAGE', 1);
            this.$store.dispatch('loadUserAnalytics');
        },
        getSortDirection(columnId) {
            return this.sortColumns[columnId]?.direction || null;
        },
        async changePerPage(event) {
            const newPerPage = parseInt(event.target.value);
            this.$store.commit('SET_USER_ANALYTICS_PER_PAGE', newPerPage);
            await this.$store.dispatch('loadUserAnalytics');
        },
        async handleExport() {
            await this.$store.dispatch('exportUserAnalytics');
        },
        async handlePageChange(action) {
            const pageActions = {
                first: () => 1,
                prev: () => Math.max(1, this.currentPage - 1),
                next: () => Math.min(this.totalPages, this.currentPage + 1),
                last: () => this.totalPages,
            };

            const newPage = pageActions[action]?.() ?? this.currentPage;
            await this.$store.dispatch('changeAnalyticsPage', newPage);
        },
    },
};
</script>

<template>
    <div class="analytics_list_container">
        <div class="right_header">
            <label>User Analytics ({{ pagination.total }})</label>
            <div class="header_actions">
                <button
                    class="export_button"
                    :disabled="isLoading || isExporting"
                    @click="handleExport"
                >
                    {{ isExporting ? 'Exporting...' : 'Export CSV' }}
                </button>
            </div>
        </div>

        <RefreshStatusCard
            :refreshStatus="refreshStatus"
            :isRefreshing="isRefreshing"
        />

        <GeneralAnalyticsFilters />

        <div v-if="isLoading" class="loading_container">
            <p>Loading analytics...</p>
        </div>

        <div v-else-if="analytics.length === 0" class="empty_container">
            <p>No analytics data found</p>
        </div>

        <div v-else>
            <div class="table_scroll_container">
                <table>
                    <thead>
                        <tr>
                            <th
                                @click="handleSort('userIdSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('userIdSort') !== null,
                                }"
                            >
                                ID
                                <span
                                    v-if="
                                        getSortDirection('userIdSort') === 'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('userIdSort') === 'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('emailSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('emailSort') !== null,
                                }"
                            >
                                Email
                                <span
                                    v-if="
                                        getSortDirection('emailSort') === 'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('emailSort') === 'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th>Status</th>
                            <th
                                @click="handleSort('downloadsSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('downloadsSort') !==
                                        null,
                                }"
                            >
                                Downloads
                                <span
                                    v-if="
                                        getSortDirection('downloadsSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('downloadsSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('uniqueDownloadsSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('uniqueDownloadsSort') !==
                                        null,
                                }"
                            >
                                Unique Downloads
                                <span
                                    v-if="
                                        getSortDirection('uniqueDownloadsSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('uniqueDownloadsSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('collectionsSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('collectionsSort') !==
                                        null,
                                }"
                            >
                                Collections
                                <span
                                    v-if="
                                        getSortDirection('collectionsSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('collectionsSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('zipDownloadsSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('zipDownloadsSort') !==
                                        null,
                                }"
                            >
                                ZIP Downloads
                                <span
                                    v-if="
                                        getSortDirection('zipDownloadsSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('zipDownloadsSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('activeDaysSort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('activeDaysSort') !==
                                        null,
                                }"
                            >
                                Active Days
                                <span
                                    v-if="
                                        getSortDirection('activeDaysSort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('activeDaysSort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th
                                @click="handleSort('lastActivitySort')"
                                :class="{
                                    sortable: true,
                                    sorted:
                                        getSortDirection('lastActivitySort') !==
                                        null,
                                }"
                            >
                                Last Activity
                                <span
                                    v-if="
                                        getSortDirection('lastActivitySort') ===
                                        'asc'
                                    "
                                    class="sort_indicator"
                                    >▲</span
                                >
                                <span
                                    v-if="
                                        getSortDirection('lastActivitySort') ===
                                        'desc'
                                    "
                                    class="sort_indicator"
                                    >▼</span
                                >
                            </th>
                            <th>Engagement</th>
                            <th>Recency</th>
                            <th>Plan</th>
                        </tr>
                    </thead>
                    <tbody>
                        <GeneralAnalyticsObject
                            v-for="record in analytics"
                            :key="record.user_id"
                            :record="record"
                        />
                    </tbody>
                </table>
            </div>

            <div class="pagination_container">
                <div class="pagination_info">
                    <span>Showing {{ displayRange }} users</span>

                    <div class="items_per_page">
                        <label>Items per page:</label>
                        <select
                            :value="pagination.perPage"
                            @change="changePerPage"
                            :disabled="isLoading"
                        >
                            <option :value="10">10</option>
                            <option :value="25">25</option>
                            <option :value="50">50</option>
                            <option :value="100">100</option>
                        </select>
                    </div>
                </div>

                <PaginationController
                    :currentPage="currentPage - 1"
                    :maxPage="maxPage"
                    @first="handlePageChange('first')"
                    @back="handlePageChange('prev')"
                    @next="handlePageChange('next')"
                    @last="handlePageChange('last')"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
.analytics_list_container {
    padding: 20px;
}

.right_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-color);
}

.loading_container,
.empty_container {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color-secondary);
}

.table_scroll_container {
    overflow-x: auto;
    overflow-y: visible;
    margin: 20px 0;
    border: 1px solid var(--border-color);
    border-radius: 8px;
}

table {
    width: 100%;
    min-width: 1100px;
    border-collapse: collapse;
}

th {
    background: var(--background-hover);
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: var(--text-color);
    border-bottom: 2px solid var(--border-color);
    white-space: nowrap;
}

th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background 0.2s ease;
}

th.sortable:hover {
    background: var(--primary-color);
    color: white;
}

th.sorted {
    background: var(--primary-color);
    color: white;
}

.sort_indicator {
    margin-left: 5px;
    font-size: 10px;
}

.pagination_container {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.pagination_info {
    display: flex;
    align-items: center;
    gap: 20px;
    color: var(--text-color);
}

.items_per_page {
    display: flex;
    align-items: center;
    gap: 8px;
}

.items_per_page label {
    font-size: 14px;
    color: var(--text-color);
}

.items_per_page select {
    padding: 6px 10px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    cursor: pointer;
}

.items_per_page select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
